import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Modal from "../modal/Modal";
import Toggle from "../form-item/Toggle";
import IconButton from "../button/IconButton";
import ToolTip from "../tooltip/ToolTip";
import DateInput from "../calendar/DateInput";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";

/** 共有対象（ファイル or フォルダ） */
export interface FileBrowserShareTarget {
  type: "file" | "folder";
  id: string;
  name: string;
}

/** 共有リンクの状態（BE レスポンスの共通サブセット） */
export interface FileBrowserShareLinkInfo {
  token: string;
  expires_at: string | null;
  require_membership: boolean;
}

export interface FileBrowserShareSettings {
  /** YYYY-MM-DD / null = 無期限 */
  expires_at: string | null;
  require_membership: boolean;
}

/**
 * 共有リンク API のアダプタ。データソース（トークルーム / ライブラリ等）ごとに
 * get / create / delete を注入して使う。
 */
export interface FileBrowserShareAdapter {
  getLink: (
    // eslint-disable-next-line no-unused-vars
    target: FileBrowserShareTarget,
  ) => Promise<FileBrowserShareLinkInfo | null>;
  createLink: (
    // eslint-disable-next-line no-unused-vars
    target: FileBrowserShareTarget,
    // eslint-disable-next-line no-unused-vars
    settings: FileBrowserShareSettings,
  ) => Promise<FileBrowserShareLinkInfo>;
  // eslint-disable-next-line no-unused-vars
  deleteLink: (target: FileBrowserShareTarget) => Promise<void>;
  /** 有効期限の初期値（YYYY-MM-DD。プロジェクトの開発完了日など） */
  defaultExpiryDate?: string | null;
  /** 共有ON/OFFが変わったときの通知（バッジのローカル反映用） */
  // eslint-disable-next-line no-unused-vars
  onShareStateChanged?: (target: FileBrowserShareTarget, isShared: boolean) => void;
}

export interface FileBrowserShareModalProps {
  target: FileBrowserShareTarget;
  adapter: FileBrowserShareAdapter;
  onClose: () => void;
}

// MCP（マイページ）の URL 表示と同デザイン
const URLText = styled.div`
  flex: 1;
  font-family: monospace;
  font-size: 11px;
  border-radius: 2px;
  border: 1px solid var(--soremo-border);
  background-color: var(--soremo-background);
  padding: 12px 4px;
  color: var(--soremo-light-gray);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HintText = styled.p.attrs({ className: "bodytext-11" })`
  margin: 0;
  color: var(--soremo-light-gray);
`;

/** 今日から30日後の Date（既定の有効期限が無い場合のフォールバック） */
const fallbackExpiryDate = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d;
};

/**
 * 有効期限の編集初期値へのクランプ。過去日（完了済みプロジェクトの完了日や
 * 期限切れリンクの日付）をそのまま使うと発行直後から期限切れのリンクに
 * なるため、今日より前の場合はフォールバック（+30日）に置き換える。
 */
const clampExpiryDate = (d: Date | null): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!d || Number.isNaN(d.getTime()) || d < today) return fallbackExpiryDate();
  return d;
};

/** アダプタの既定有効期限（YYYY-MM-DD）を初期値に変換する */
const initialExpiryDate = (defaultDate?: string | null): Date =>
  clampExpiryDate(defaultDate ? new Date(`${defaultDate}T00:00:00`) : null);

/** Date → YYYY-MM-DD（BE へ送る形式） */
const toDateString = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// カレンダーを input 直下（static 配置）ではなく body 直下に描画する。
// モーダル本体は overflow: hidden のため、static 配置だとはみ出した部分が
// 隠れてしまう（オプション object は再レンダーで identity が変わらないよう固定）。
const CALENDAR_OPTIONS = { static: false };

interface ShareSettingsState {
  expiryEnabled: boolean;
  expiryDate: Date;
  membershipRequired: boolean;
}

/**
 * 共有リンク管理モーダル（汎用版）。トークルームの ShareLinkModal と同じ UX で、
 * リンクの取得・発行・解除をアダプタ経由で行う。
 *
 * - 共有トグル（先頭）: ONでリンク発行、OFFで共有解除
 * - 有効期限トグル（既定オン。アダプタの defaultExpiryDate があらかじめ設定される）
 * - アクセス制限トグル（既定オン = メンバーのみ。オフで誰でもアクセス可能）
 * - 発行されたURLの表示とコピー
 *
 * リンクは対象ごとに1本で、設定変更は同じURLのまま即時反映される。
 */
const FileBrowserShareModal: React.FC<FileBrowserShareModalProps> = ({
  target,
  adapter,
  onClose,
}) => {
  const [initialized, setInitialized] = useState(false);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [settings, setSettings] = useState<ShareSettingsState>({
    expiryEnabled: true,
    expiryDate: initialExpiryDate(adapter.defaultExpiryDate),
    membershipRequired: true,
  });
  const [token, setToken] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const { copy: copyToClipboard } = useCopyToClipboard({
    successMessage: "共有URLをコピーしました",
  });

  // 既存リンクの取得（共有中なら設定を復元して表示）
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const link = await adapter.getLink(target);
        if (cancelled) return;
        if (link) {
          setShareEnabled(true);
          setToken(link.token);
          setSettings((prev) => ({
            expiryEnabled: link.expires_at !== null,
            expiryDate: link.expires_at
              ? clampExpiryDate(new Date(link.expires_at))
              : prev.expiryDate,
            membershipRequired: link.require_membership,
          }));
        }
      } catch {
        if (!cancelled) toast.error("共有リンクの取得に失敗しました");
      } finally {
        if (!cancelled) setInitialized(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // 対象が変わったときのみ再取得する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.type, target.id]);

  // リンクの発行 / 設定更新（同じトークンのまま更新される）
  const issueLink = useCallback(
    async (next: ShareSettingsState): Promise<boolean> => {
      setIsBusy(true);
      try {
        const link = await adapter.createLink(target, {
          expires_at: next.expiryEnabled ? toDateString(next.expiryDate) : null,
          require_membership: next.membershipRequired,
        });
        setToken(link.token);
        return true;
      } catch (err) {
        const message =
          (err as { data?: { message?: string } })?.data?.message ||
          "共有リンクの発行に失敗しました";
        toast.error(message);
        return false;
      } finally {
        setIsBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [target.type, target.id, adapter],
  );

  // 共有トグル: ON = 発行 / OFF = 解除
  const handleShareToggle = useCallback(async () => {
    if (isBusy || !initialized) return;
    if (!shareEnabled) {
      const ok = await issueLink(settings);
      if (ok) {
        setShareEnabled(true);
        adapter.onShareStateChanged?.(target, true);
      }
      return;
    }
    setIsBusy(true);
    try {
      await adapter.deleteLink(target);
      setShareEnabled(false);
      setToken(null);
      adapter.onShareStateChanged?.(target, false);
      toast.success("共有を解除しました");
    } catch {
      toast.error("共有の解除に失敗しました");
    } finally {
      setIsBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusy, initialized, shareEnabled, settings, issueLink, adapter]);

  // 設定変更（共有中は即時にリンクへ反映する）
  const applySettings = useCallback(
    (patch: Partial<ShareSettingsState>) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        if (shareEnabled) issueLink(next);
        return next;
      });
    },
    [shareEnabled, issueLink],
  );

  const shareUrl =
    shareEnabled && token ? `${window.location.origin}/share/${token}` : null;

  return (
    <Modal
      title="共有"
      titleSize="medium"
      size="default"
      description={
        target.type === "folder" ? `フォルダ「${target.name}」` : target.name
      }
      onClose={onClose}
    >
      <div className="u-w100 u-col u-gap24">
        {/* 共有 ON/OFF */}
        <div className="u-w100 u-col u-gap8">
          <Toggle value={shareEnabled} onClick={handleShareToggle}>
            <span className="bodytext-13">共有</span>
          </Toggle>
          <HintText>
            {shareEnabled
              ? "共有中です。オフにすると発行済みのリンクは無効になります"
              : "オンにすると共有リンクが発行されます"}
          </HintText>
        </div>

        {/* 共有中のみ設定とURLを表示する */}
        {shareEnabled && (
          <>
            {/* 有効期限 */}
            <div className="u-w100 u-col u-gap8">
              <Toggle
                value={settings.expiryEnabled}
                onClick={() =>
                  applySettings({ expiryEnabled: !settings.expiryEnabled })
                }
              >
                <span className="bodytext-13">有効期限</span>
              </Toggle>
              {settings.expiryEnabled ? (
                <DateInput
                  value={settings.expiryDate}
                  minDate={toDateString(new Date())}
                  width="240px"
                  options={CALENDAR_OPTIONS}
                  onChange={(e) => {
                    if (e.target.value instanceof Date) {
                      applySettings({ expiryDate: e.target.value });
                    }
                  }}
                />
              ) : (
                <HintText>期限なしでアクセスできます</HintText>
              )}
            </div>

            {/* アクセス制限 */}
            <div className="u-w100 u-col u-gap8">
              <Toggle
                value={settings.membershipRequired}
                onClick={() =>
                  applySettings({
                    membershipRequired: !settings.membershipRequired,
                  })
                }
              >
                <span className="bodytext-13">アクセス制限</span>
              </Toggle>
              <HintText>
                {settings.membershipRequired
                  ? "プロジェクトメンバーのみアクセスできます"
                  : "リンクを知っていれば、サインインしていないユーザーもアクセスできます"}
              </HintText>
            </div>

            {/* URL表示とコピー（MCPと同デザイン） */}
            <div className="u-w100 u-row u-gap4">
              <URLText>{shareUrl ?? (isBusy ? "リンクを発行中..." : "")}</URLText>
              <ToolTip
                description="共有URLをクリップボードにコピー"
                placement="top"
              >
                <IconButton
                  icon="content_copy"
                  size={16}
                  disabled={!shareUrl}
                  onClick={() => shareUrl && copyToClipboard(shareUrl)}
                />
              </ToolTip>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default FileBrowserShareModal;
